import type { Issue, ProjectMeta } from './types';
import { checklistComplete, checklistPromptsOf } from './checklist-prompts';

/**
 * Whether an issue may be called done, and what is stopping it.
 *
 * A workflow gate, not a permission: everyone who can edit an issue can also
 * tick its boxes. The point is not to stop people, it is to stop "done" meaning
 * four different things on the same board — and to make what is missing
 * obvious at the moment somebody tries to move the card, rather than at the
 * release, when it is expensive.
 */
export function outstandingChecks(issue: Pick<Issue, 'checks'>, meta: ProjectMeta): string[] {
  const required = meta.doneChecks ?? [];
  if (required.length === 0) return [];
  return required.filter((c) => issue.checks?.[c] !== true);
}

export function mayBeDone(issue: Pick<Issue, 'checks'>, meta: ProjectMeta): boolean {
  return outstandingChecks(issue, meta).length === 0;
}

/**
 * WHETHER A CARD MAY BE MOVED TO DONE — the gate the UI surfaces enforce.
 *
 * Two ways to satisfy it: the board's Definition of Done is met (`mayBeDone`), OR
 * the card's own checklist is fully ticked. A 100%-worked checklist is the truest
 * read that a card is complete (progress-derive.ts), and "the DoD will not get used
 * much" (Vinnie, 28 Oct), so a full checklist clears the gate even when the DoD is
 * not ticked — UNLESS the board has turned its checklist prompts off, in which case
 * the DoD stands alone as the sole hard gate (its `onComplete` flag is the one that
 * governs the finish, the same flag that governs the 100% offer).
 *
 * This is DISTINCT from `outstandingChecks`, which still reports the real DoD state
 * for display: a card let through here on its checklist can be moved to Done, yet
 * its DoD panel truthfully shows which shared checks are still open.
 */
export function doneAllowed(
  issue: Pick<Issue, 'checks' | 'checklist'>,
  meta: ProjectMeta,
): boolean {
  if (mayBeDone(issue, meta)) return true;
  const prompts = checklistPromptsOf(meta.checklistPrompts);
  return prompts.on && prompts.onComplete && checklistComplete(issue);
}

/**
 * The default list, offered to a project that has never set one.
 *
 * Deliberately short and deliberately about evidence rather than ceremony. A
 * long list gets ticked without reading, which is worse than no list because it
 * looks like assurance.
 */
export const SUGGESTED_CHECKS = ['Code reviewed', 'Tested', 'Notes updated'];
